import React, { useEffect, useState } from "react";
import Card from "./ArtCard";
import JSZip from "jszip";
import { useArtworks } from "../../hooks/useSanityData";
import { getImageFileName } from "../../lib/sanity";
import { logger } from "../../lib/logger";
import { korusOrder } from "../../data/korusOrder";

// Extract first year from a date string for sorting
const extractYear = (dateString) => {
  return parseInt(dateString.split("/")[0].split("~")[0].trim());
};

// Extract all years from a date string (handles ranges like "-1500/-1400" or "1500/1510")
const extractAllYears = (dateString) => {
  const matches = dateString.match(/-?\d+/g);
  return matches ? matches.map((y) => Math.abs(parseInt(y))) : [];
};

// Compare two artworks by their starting year for the Date sort. Artworks with
// a missing/unparseable date (extractYear -> NaN, e.g. when Sanity omits the
// dateRange) always sort to the end regardless of direction, instead of feeding
// NaN into the comparator and producing a non-deterministic order.
const compareByYear = (aDate, bDate, descending) => {
  const aYear = extractYear(aDate);
  const bYear = extractYear(bDate);
  const aMissing = Number.isNaN(aYear);
  const bMissing = Number.isNaN(bYear);
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return descending ? bYear - aYear : aYear - bYear;
};

// Calculate relevance score for smart search ranking
const calculateRelevanceScore = (item, searchTerm) => {
  if (!searchTerm) return 0;

  const term = searchTerm.toLowerCase().trim();
  const termAsNumber = parseInt(term, 10);
  const isNumericSearch = !isNaN(termAsNumber);
  let score = 0;

  // Priority 1: Exact ID match (highest priority)
  if (isNumericSearch && item.id === termAsNumber) {
    score += 10000;
  }
  // Priority 2: ID starts with search term
  else if (isNumericSearch && item.id.toString().startsWith(term)) {
    score += 5000;
  }
  // Priority 3: ID contains search term
  else if (item.id.toString().includes(term)) {
    score += 2000;
  }

  // Priority 4: Name starts with search term (case insensitive)
  const nameLower = (item.name || "").toLowerCase();
  if (nameLower.startsWith(term)) {
    score += 1500;
  }
  // Priority 5: Name contains search term as a word boundary
  else if (nameLower.includes(` ${term}`) || nameLower.includes(`${term} `)) {
    score += 1000;
  }
  // Priority 6: Name contains search term anywhere
  else if (nameLower.includes(term)) {
    score += 500;
  }

  // Priority 7: Year/date matches (for numeric searches)
  if (isNumericSearch) {
    const years = extractAllYears(item.date);
    // Exact year match
    if (years.includes(termAsNumber)) {
      score += 400;
    }
    // Year starts with term (e.g., "15" matches 1500, 1510, etc.)
    else if (years.some((y) => y.toString().startsWith(term))) {
      score += 300;
    }
    // Year contains term
    else if (years.some((y) => y.toString().includes(term))) {
      score += 200;
    }
  }

  // Priority 8: Location match
  const locationLower = (item.location || "").toLowerCase();
  if (locationLower.startsWith(term)) {
    score += 150;
  } else if (locationLower.includes(term)) {
    score += 100;
  }

  // Priority 9: Artist/culture match
  const artistLower = (item.artist_culture || "").toLowerCase();
  if (artistLower.startsWith(term)) {
    score += 80;
  } else if (artistLower.includes(term)) {
    score += 50;
  }

  // Priority 10: Materials match
  const materialsLower = (item.materials || "").toLowerCase();
  if (materialsLower.includes(term)) {
    score += 30;
  }

  // Priority 11: Museum match
  const museumLower = (item.museum || "").toLowerCase();
  if (museumLower.includes(term)) {
    score += 20;
  }

  // Priority 12: Transcript match (lowest priority - fallback search)
  if (item.transcript && score === 0) {
    try {
      const transcriptText = item.transcript
        .map((t) => JSON.parse(t))
        .flat()
        .map((segment) => segment.text)
        .join(" ")
        .toLowerCase();
      if (transcriptText.includes(term)) {
        score += 10;
      }
    } catch (e) {
      // Skip if transcript parsing fails
    }
  }

  return score;
};

function Catalog({ search, layout, setLayout, sort, unitFilters }) {
  const [currPageNumber, setCurrPageNumber] = useState(1);
  const [fullArtPiecesArray, setFullArtPiecesArray] = useState([]);
  const [artPiecesArray, setLocalArtPiecesArray] = useState([]);

  // Fetch artworks from Sanity CMS
  const { artworks: artPiecesData, loading, error } = useArtworks();

  // Get image path - now images are direct URLs from Sanity
  const getImagePath = (imageUrl) => {
    // If it's already a URL (from Sanity), return it directly
    if (imageUrl && imageUrl.startsWith('http')) {
      return imageUrl;
    }
    return imageUrl || "";
  };

  // Initialize art pieces data when loaded from Sanity
  useEffect(() => {
    if (artPiecesData.length > 0) {
      setFullArtPiecesArray(artPiecesData);
      setLocalArtPiecesArray(artPiecesData);
    }
  }, [artPiecesData]);

  useEffect(() => {
    // O(1) access of the index of the art piece in the korusOrder
    const korusMap = new Map(korusOrder.map((id, index) => [id, index]));

    // Calculate relevance scores for all items when there's a search term
    const itemsWithScores = fullArtPiecesArray.map((item) => ({
      ...item,
      relevanceScore: search ? calculateRelevanceScore(item, search) : 0,
    }));

    let filteredArtPieces = itemsWithScores
      .filter((item) => {
        // If no search term, show all
        if (!search) return true;

        // Only show items with a relevance score > 0
        return item.relevanceScore > 0;
      })
      .sort((a, b) => {
        // When searching and sort is "Relevance" (or default), sort by relevance score
        if (search && (sort === "Relevance" || sort === "ID Ascending")) {
          const scoreDiff = b.relevanceScore - a.relevanceScore;
          if (scoreDiff !== 0) return scoreDiff;
          // Secondary sort by ID for items with same relevance
          return a.id - b.id;
        }

        switch (sort) {
          case "Name Descending":
            return (b.name || "").localeCompare(a.name || "");
          case "Name Ascending":
            return (a.name || "").localeCompare(b.name || "");
          case "Unit Descending":
          case "Content Area Descending":
            return b.unit - a.unit;
          case "Unit Ascending":
          case "Content Area Ascending":
            return a.unit - b.unit;
          case "ID Descending":
            return b.id - a.id;
          case "ID Ascending":
            return a.id - b.id;
          case "Date Descending":
            return compareByYear(a.date, b.date, true);
          case "Date Ascending":
            return compareByYear(a.date, b.date, false);
          case "Korus Sort": {
            // IDs not present in korusOrder (e.g. new Sanity artworks) sort to
            // the end deterministically instead of producing a NaN comparator.
            const fallback = korusOrder.length;
            return (
              (korusMap.get(a.id) ?? fallback) - (korusMap.get(b.id) ?? fallback)
            );
          }
          case "Relevance":
            // When no search, relevance sort falls back to ID
            return a.id - b.id;
          default:
            return a.id - b.id;
        }
      });

    if (!Object.values(unitFilters).every((value) => !value)) {
      filteredArtPieces = filteredArtPieces.filter(
        (item) => unitFilters[`unit${item.unit}`]
      );
    }

    setLocalArtPiecesArray(filteredArtPieces);

    if (currPageNumber > Math.ceil(filteredArtPieces.length / itemsPerPage)) {
      setCurrPageNumber(1);
    }
    // currPageNumber is deliberately omitted from the deps: the sorted/filtered
    // list does not depend on the page (pagination slices artPiecesArray at
    // render time), and the clamp above reads the live value via closure. Adding
    // it re-ran the relevance scorer + transcript parsing across the whole
    // catalog on every page click for an identical result.
  }, [search, sort, unitFilters, fullArtPiecesArray]);

  // Changes the page number
  const handlePageClick = (pageNum) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCurrPageNumber(pageNum);
  };

  const itemsPerPage = 50;
  const startIndex = (currPageNumber - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentArtPieces = artPiecesArray.slice(startIndex, endIndex);
  const totalPages = Math.ceil(artPiecesArray.length / itemsPerPage);

  const handleDownloadAllArtworks = async () => {
    const zip = new JSZip();
    let totalImages = 0;
    let processedImages = 0;

    // Create a status message element
    const statusElement = document.createElement("div");
    statusElement.className =
      "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1000] w-[90%] max-w-[300px] text-center p-4 sm:p-6 bg-[var(--foreground-color)] rounded-xl shadow-xl";
    document.body.appendChild(statusElement);
    statusElement.innerHTML =
      '<p class="text-[var(--text-color)]">Preparing to download all images...</p>';

    try {
      // Count total images first
      artPiecesData.forEach((artPiece) => {
        if (artPiece.image && Array.isArray(artPiece.image)) {
          totalImages += artPiece.image.length;
        }
      });

      statusElement.innerHTML = `<p class="text-[var(--text-color)]">Starting download of ${totalImages} images...</p>`;

      // Process each artwork
      for (const artPiece of artPiecesData) {
        if (
          !artPiece.image ||
          !Array.isArray(artPiece.image) ||
          artPiece.image.length === 0
        ) {
          continue;
        }

        // Create a folder for each artwork
        const folderName = `${artPiece.id}_${(artPiece.name || "").replace(
          /[^\w\s]/gi,
          ""
        )}`;

        // Process each image for this artwork
        for (const imageName of artPiece.image) {
          if (!imageName) continue;

          const imagePath = getImagePath(imageName);
          if (!imagePath) continue;

          try {
            const response = await fetch(imagePath);
            const blob = await response.blob();
            const fileName = getImageFileName(
              imageName,
              `image_${processedImages + 1}.jpg`
            );
            zip.file(`${folderName}/${fileName}`, blob);

            processedImages++;
            statusElement.innerHTML = `<p class="text-[var(--text-color)]">Downloaded ${processedImages} of ${totalImages} images...</p>`;
          } catch (error) {
            logger.error(`Failed to fetch image ${imageName}:`, error);
          }
        }
      }

      statusElement.innerHTML =
        '<p class="text-[var(--text-color)]">Creating ZIP file...</p>';

      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: "blob" });

      // Create a download link for the zip file
      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);
      link.download = "all_artwork_images.zip";
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      statusElement.innerHTML =
        '<p class="text-[var(--text-color)]">Download complete!</p>';

      // Remove status element after a short delay
      setTimeout(() => {
        document.body.removeChild(statusElement);
      }, 3000);
    } catch (error) {
      logger.error("Failed to create ZIP file:", error);
      statusElement.innerHTML = `<p class="text-red-400">Error: ${error.message}</p>`;

      // Remove status element after a longer delay for error messages
      setTimeout(() => {
        document.body.removeChild(statusElement);
      }, 5000);
    }
  };

  // Create image object for lazy loading
  const getImage = (item) => {
    if (!item.image || !item.image[0]) return null;
    const img = new Image();
    img.src = getImagePath(item.image[0]);
    return img;
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-pulse text-lg text-[var(--text-muted)]">
            Loading artworks...
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="text-lg text-[var(--fc-bad-text)]">
            Failed to load artworks. Please try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-2">
      {/* Count line + grid/table toggle */}
      <div className="flex items-center justify-between mb-4 px-0.5">
        <p className="text-sm text-[var(--text-muted)]">
          Showing{" "}
          <span className="text-[var(--gold-soft)]">{currentArtPieces.length}</span>
          {" "}of{" "}
          <span className="text-[var(--gold-soft)]">{artPiecesArray.length}</span>
          {" "}artworks
          {artPiecesArray.length !== artPiecesData.length && (
            <span className="text-[var(--text-muted)]"> (filtered from {artPiecesData.length} total)</span>
          )}
        </p>

        {/* Grid / Table toggle */}
        <div
          className="flex border border-[var(--border-soft)] rounded overflow-hidden"
          role="group"
          aria-label="View layout"
        >
          <button
            className={`px-3 py-2 text-[13px] transition-colors
                       focus:outline-none focus:ring-2 focus:ring-[var(--gold-color)] focus:ring-inset
                       ${layout === "grid"
                         ? "bg-[var(--surface-2)] text-[var(--gold-soft)]"
                         : "bg-transparent text-[var(--text-muted)] hover:bg-[var(--surface-1)]"
                       }`}
            onClick={() => setLayout("grid")}
            aria-pressed={layout === "grid"}
            aria-label="Grid view"
          >
            ▦ Grid
          </button>
          <button
            className={`px-3 py-2 text-[13px] transition-colors
                       focus:outline-none focus:ring-2 focus:ring-[var(--gold-color)] focus:ring-inset
                       ${layout === "table"
                         ? "bg-[var(--surface-2)] text-[var(--gold-soft)]"
                         : "bg-transparent text-[var(--text-muted)] hover:bg-[var(--surface-1)]"
                       }`}
            onClick={() => setLayout("table")}
            aria-pressed={layout === "table"}
            aria-label="Table view"
          >
            ≣ Table
          </button>
        </div>
      </div>

      {/* Art cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-[18px]">
        {currentArtPieces.map((item) => (
          <Card
            key={item.id}
            item={item}
            layout={layout}
            image={getImage(item)}
            search={search.toLowerCase()}
          />
        ))}
        {artPiecesArray.length === 0 && (
          <div className="col-span-full flex flex-col items-center justify-center py-16 text-center">
            <div className="text-5xl mb-4 text-[var(--text-muted)]">—</div>
            <h3 className="font-display text-xl text-[var(--text-strong)] mb-2">
              No artworks found
            </h3>
            <p className="text-sm text-[var(--text-muted)] max-w-md">
              Try adjusting your search or filters.
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav
          className="flex items-center justify-center gap-2 mt-8 flex-wrap"
          aria-label="Pagination"
        >
          {[...Array(totalPages).keys()].map((pageNum) => {
            const isActive = currPageNumber === pageNum + 1;
            return (
              <button
                key={pageNum}
                className={`min-h-[44px] min-w-[44px] px-4 py-2 rounded-lg text-sm font-medium transition-colors
                           focus:outline-none focus:ring-2 focus:ring-[var(--gold-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]
                           ${
                             isActive
                               ? "bg-[var(--gold-color)] text-[var(--ink-on-gold)]"
                               : "bg-[var(--surface-1)] border border-[var(--border-soft)] text-[var(--text-muted)] hover:border-[var(--border-gold)] hover:text-[var(--text-default)]"
                           }`}
                onClick={() => handlePageClick(pageNum + 1)}
                aria-current={isActive ? "page" : undefined}
                aria-label={`Page ${pageNum + 1}`}
              >
                {pageNum + 1}
              </button>
            );
          })}
        </nav>
      )}

      {/* Download all button */}
      <div className="flex flex-col items-center justify-center py-8">
        <button
          className="px-6 py-3
                     bg-[var(--surface-1)] border border-[var(--border-gold)]
                     text-[var(--text-default)] rounded-lg font-medium text-sm
                     hover:bg-[var(--surface-2)] hover:text-[var(--text-strong)]
                     transition-colors duration-200
                     focus:outline-none focus:ring-2 focus:ring-[var(--gold-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
          onClick={handleDownloadAllArtworks}
        >
          Download All Artwork Images as ZIP
        </button>
        <p className="text-xs text-[var(--text-muted)] mt-2">
          This will download all images from all {artPiecesData.length}{" "}
          artworks.
        </p>
      </div>
    </div>
  );
}

export default Catalog;
